import React from 'react';
import { useNavigate } from 'react-router-dom';
import CardMarketPriceDisplay from '../../../components/CardMarketPriceDisplay';
import { Avatar, Box, Card, Chip, IconButton, Typography } from '@mui/material';
import { MoreVert } from '@mui/icons-material';
import { FormatUtils } from '@mega-pokedex/shared';
import { EnrichedOwnership } from '../../../hooks/useEnrichedCollection';
import GradedSlab, { parseGradingInfo } from '../../../components/GradedSlab';

interface CollectionCardProps {
  card: EnrichedOwnership;
  onMenuClick: (event: React.MouseEvent<HTMLElement>, cardId: string) => void;
}

const CollectionCard: React.FC<CollectionCardProps> = ({
  card,
  onMenuClick,
}) => {
  const navigate = useNavigate();

  const parsedGrading = parseGradingInfo(card.condition, card.notes);
  const isGraded = card.isGrading || !!card.gradingCompany || card.location?.toLowerCase().includes('grade') || !!parsedGrading;
  const gradingInfo = parsedGrading || (card.gradingCompany ? { company: card.gradingCompany, grade: card.gradingGrade || '10' } : null);

  const handleCardClick = () => {
    // Navigate to collection card detail page with setId and cardId, passing card data via state
    // Use setId from data if available (e.g. for Pokemon 'base1'), otherwise fallback to setName
    const setId = card.setId || card.setName;
    navigate(`/collection/${setId}/${card.id}`, {
      state: { card, fromPath: '/collection', fromLabel: 'Collection' },
    });
  };

  const handleMenuClick = (e: React.MouseEvent<HTMLElement>) => {
    e.stopPropagation(); // Prevent card click when clicking menu
    onMenuClick(e, card.id);
  };

  return (
    <Card
      sx={{
        height: '100%',
        position: 'relative',
        display: 'flex',
        flexDirection: 'column',
        cursor: 'pointer',
        transition: 'transform 0.2s, box-shadow 0.2s',
        '&:hover': {
          transform: 'translateY(-4px)',
          boxShadow: 4,
        },
      }}
      onClick={handleCardClick}
    >
      {/* Menu Button (Shared) */}
      <Box sx={{ position: 'absolute', top: 8, right: 8, zIndex: 2 }}>
        <IconButton
          size="small"
          onClick={handleMenuClick}
          sx={{ bgcolor: 'background.paper', boxShadow: 1 }}
        >
          <MoreVert />
        </IconButton>
      </Box>

      {/* MOBILE LAYOUT: Side-by-side */}
      <Box sx={{ display: { xs: 'flex', md: 'none' }, p: 1.5, gap: 1.5, flex: 1 }}>
        {/* Card Image */}
        <Box sx={{ flexShrink: 0, width: 110, height: 154 }}>
          {isGraded ? (
            <GradedSlab
              imageUrl={card.imageUrl}
              cardName={card.justtcgName || card.cardName}
              setName={card.setName}
              number={card.number}
              gradingCompany={gradingInfo?.company}
              gradingGrade={gradingInfo?.grade}
              height="100%"
            />
          ) : (
            <Avatar
              src={card.imageUrl}
              alt={card.cardName}
              sx={{
                width: '100%',
                height: '100%',
                borderRadius: 1.5,
                border: '1px solid',
                borderColor: 'divider',
                backgroundColor: 'grey.200',
              }}
              variant="rounded"
            />
          )}
        </Box>

        {/* Card Info */}
        <Box
          sx={{
            flex: 1,
            minWidth: 0,
            display: 'flex',
            flexDirection: 'column',
            gap: 0.5,
          }}
        >
          <Box pr={3}>
            <Typography variant="subtitle2" sx={{ fontWeight: 'bold', lineHeight: 1.2, mb: 0.25 }} noWrap>
              {card.justtcgName || card.cardName}
            </Typography>
            <Typography variant="caption" color="textSecondary" display="block" noWrap>
              {card.setName} • #{card.number}
            </Typography>
          </Box>

          <Box display="flex" gap={0.5} flexWrap="wrap" alignItems="center">
            <Chip label={card.game === 'pokemon' ? 'PKMN' : 'OP'} size="small" color={card.game === 'pokemon' ? 'primary' : 'secondary'} sx={{ height: 20, fontSize: '0.65rem' }} />
            <Chip label={card.language === 'JP' ? 'JP' : 'EN'} size="small" variant="outlined" color={card.language === 'JP' ? 'warning' : 'default'} sx={{ height: 20, fontSize: '0.65rem' }} />
            <Chip label={card.condition.replace('_', ' ')} size="small" variant="outlined" sx={{ height: 20, fontSize: '0.65rem' }} />
            <Chip label={`Qty: ${card.quantity}`} size="small" variant="outlined" sx={{ height: 20, fontSize: '0.65rem' }} />
            {card.rarity && <Chip label={card.rarity} size="small" sx={{ height: 20, fontSize: '0.65rem' }} />}
          </Box>

          <Box display="flex" gap={2} mt="auto">
            <Box flex={1}>
              <Typography variant="caption" color="textSecondary" display="block" sx={{ fontSize: '0.65rem' }}>Cost</Typography>
              <Typography variant="body2" sx={{ fontWeight: 'bold', fontSize: '0.85rem' }}>{FormatUtils.formatCurrency(card.costBasis)}</Typography>
            </Box>
            <Box flex={1}>
              <Typography variant="caption" color="textSecondary" display="block" sx={{ fontSize: '0.65rem' }}>Value</Typography>
              <Typography variant="body2" sx={{ fontWeight: 'bold', fontSize: '0.85rem' }}>{FormatUtils.formatCurrency(card.currentValue)}</Typography>
            </Box>
          </Box>

          {card.location && (
            <Typography variant="caption" color="textSecondary" sx={{ fontSize: '0.65rem' }} noWrap>
              📍 {card.location}
            </Typography>
          )}
        </Box>
      </Box>

      {/* DESKTOP LAYOUT: Vertical Stack */}
      <Box sx={{ display: { xs: 'none', md: 'flex' }, flexDirection: 'column', flex: 1 }}>
        {/* Header - Title at Top */}
        <Box sx={{ p: 1.5, pb: 1, pr: 5, position: 'relative' }}>
          <Typography variant="subtitle2" sx={{ fontWeight: 'bold', lineHeight: 1.2, mb: 0.25 }} noWrap>
            {card.justtcgName || card.cardName}
          </Typography>
          <Typography variant="caption" color="textSecondary" display="block" noWrap>
            {card.setName} • #{card.number}
          </Typography>
        </Box>

        {/* Image in Middle */}
        <Box sx={{ px: 1.5, display: 'flex', justifyContent: 'center', height: 230, width: '100%' }}>
          {isGraded ? (
            <GradedSlab
              imageUrl={card.imageUrl}
              cardName={card.justtcgName || card.cardName}
              setName={card.setName}
              number={card.number}
              gradingCompany={gradingInfo?.company}
              gradingGrade={gradingInfo?.grade}
              height="100%"
            />
          ) : (
            <Box
              component="img"
              src={card.imageUrl}
              alt={card.cardName}
              sx={{
                width: '100%',
                maxWidth: 160,
                aspectRatio: '2.5/3.5',
                objectFit: 'contain',
                borderRadius: 1.5,
                bgcolor: 'rgba(0,0,0,0.02)',
              }}
            />
          )}
        </Box>

        {/* Details at Bottom */}
        <Box sx={{ p: 1.5, display: 'flex', flexDirection: 'column', gap: 1, flex: 1 }}>
          <Box display="flex" gap={0.5} flexWrap="wrap">
            <Chip label={card.game === 'pokemon' ? 'PKMN' : 'OP'} size="small" color={card.game === 'pokemon' ? 'primary' : 'secondary'} sx={{ height: 20, fontSize: '0.65rem' }} />
            <Chip label={card.language === 'JP' ? 'JP' : 'EN'} size="small" variant="outlined" color={card.language === 'JP' ? 'warning' : 'default'} sx={{ height: 20, fontSize: '0.65rem' }} />
            <Chip label={card.condition.replace('_', ' ')} size="small" variant="outlined" sx={{ height: 20, fontSize: '0.65rem' }} />
            <Chip label={`Qty: ${card.quantity}`} size="small" variant="outlined" sx={{ height: 20, fontSize: '0.65rem' }} />
            {card.rarity && <Chip label={card.rarity} size="small" sx={{ height: 20, fontSize: '0.65rem' }} />}
          </Box>

          <Box display="flex" justifyContent="space-between" alignItems="flex-end" mt="auto">
            <Box>
              <Typography variant="caption" color="textSecondary" display="block" sx={{ fontSize: '0.65rem' }}>Cost Basis</Typography>
              <Typography variant="body2" sx={{ fontWeight: 'bold', color: 'error.main' }}>{FormatUtils.formatCurrency(card.costBasis)}</Typography>
            </Box>
            <Box textAlign="right">
              <CardMarketPriceDisplay marketPrice={card.currentValue} size="small" />
            </Box>
          </Box>

          {card.location && (
            <Typography variant="caption" color="textSecondary" sx={{ fontSize: '0.65rem', mt: 0.5 }} noWrap>
              📍 {card.location}
            </Typography>
          )}
        </Box>
      </Box>
    </Card>
  );
};

export default CollectionCard;
